/*
 * Application State should be all within this file.
 */

import config from "@/data/config.json";
import { useContentStore } from "@/dev/store/useContentStore";

function getDefaultValue(type, subtype) {
  if (type === "table") {
    return [];
  }

  if (subtype === "number") {
    return 0;
  }

  return "";
}

const step = {
  state: {
    currentStepId: "", // This will track the current step the user is on
    progressStepId: "", // This will track the furthest step the user has reached
    stepValues: {}, // This will hold the user data for each step, keyed by step ID
    stepData: [], // This will hold the data for each step, which can be used to populate the UI
    isReady: false
  },
  mutations: {
    SET_STEP_DATA(state, value) {
      state.stepValues = value;
      state.isReady = true;
    },
    SET_STEP_VALUE(state, step) {
      console.log("STEP DATA:", step);
      state.stepValues[step.id] = step.value;

      console.log("Updated stepValues:", state.stepValues);
    },
    SET_CURRENT_STEP_ID(state, payload) {
      console.log("Setting current step ID to:", payload);
      state.currentStepId = payload.step;

      // Find the index of current step id and progress step id in the steps data
      const steps = payload.steps;
      const currentStepIndex = steps.findIndex((s) => s.id === payload.step);
      const progressStepIndex = steps.findIndex(
        (s) => s.id === state.progressStepId
      );

      // If the current step index is greater than the progress step index, update the progress step id
      if (currentStepIndex > progressStepIndex) {
        state.progressStepId = payload.step;
      }
    }
  },
  actions: {
    setStepData({ commit }, data) {
      commit("SET_STEP_DATA", data);
    },
    setStepValue({ commit }, step) {
      commit("SET_STEP_VALUE", step);
    },
    setCurrentStepId({ commit, getters }, step) {
      let sections = getters.getSections;
      let steps = sections.flatMap((section) => section.steps);

      commit("SET_CURRENT_STEP_ID", { step, steps });
    },
    // Action to go to the next step
    goToStep({ dispatch, getters }, { direction, id = "" }) {
      const currentStepId = id ? id : getters.getCurrentStepId;
      const steps = getters.getSections.flatMap((section) => section.steps);
      const currentIndex = steps.findIndex((step) => step.id === currentStepId);
      console.log("CURRENT INDEX", currentIndex);
      const nextIndex = currentIndex + direction;

      if (currentIndex !== -1 && nextIndex >= 0 && nextIndex < steps.length) {
        const nextStepId = steps[nextIndex].id;
        dispatch("setCurrentStepId", nextStepId);
      }
    }
  },
  getters: {
    getIsReady(state) {
      return state.isReady;
    },
    // Get the current step id
    getCurrentStepId(state) {
      return state.currentStepId;
    },
    getCurrentStepIndex(state, getters) {
      let steps = getters.getSections.flatMap((section) => section.steps);
      return steps.findIndex((step) => step.id === getters.getCurrentStepId);
    },
    getProgressStepId(state) {
      return state.progressStepId;
    },
    getStepValues(state) {
      return state.stepValues;
    },
    getTotalSteps(state, getters) {
      let sections = getters.getSections;
      return sections.reduce(
        (total, section) => total + section.steps.length,
        0
      );
    },
    // Get the current step data
    getCurrentStep(state, getters) {
      let sections = getters.getSections;
      let getCurrentStepId = getters.getCurrentStepId;

      // go through each section and combine array of steps, then find the step with the current id
      const allSteps = sections.flatMap((section) => section.steps);

      // get current step based on the current step id
      const step = allSteps.find((s) => s.id === getCurrentStepId);

      if (step) {
        // Find the section that contains this step to get its colors
        for (const section of sections) {
          const matchingStep = section.steps.find((s) => s.id === step.id);

          if (matchingStep) {
            return {
              ...step,
              backgroundColor: section.backgroundColor,
              textColor: section.textColor
            };
          }
        }
      }

      // Return null if no step found
      return null;
    },
    // Get all steps as an array
    getSteps:
      (state, getters) =>
      (values = "") => {
        const contentStore = useContentStore();
        let steps = contentStore.content;
        let sections = getters.getSections;
        let stepValues = values ? values : state.stepValues;
        let progressIndex = -1;

        console.log("Getting steps with values:", stepValues);

        steps = steps.map((step, index) => {
          const section = sections.find((s) => s.name === step.section);
          let backgroundColor = section ? section.backgroundColor : "";

          return {
            ...step, // Spread the original step data
            index,
            disabled:
              progressIndex !== -1
                ? index > progressIndex
                : (step.disabled ?? false), // If progress step is set, disable steps that come after it

            value: stepValues[step.id] || "", // Add the current user value for this step from the state
            backgroundColor: backgroundColor
          };
        });

        return steps;
      },
    // Get sections from steps data and include colors
    getSections(state) {
      const contentStore = useContentStore();
      let steps = contentStore.content;
      let sectionColors = config.appStyle.sectionColors; // Get section colors from config

      let progressIndex = -1;

      // Find the index of the progress step in the steps data
      if (state.progressStepId?.length > 0) {
        progressIndex = steps.findIndex(
          (step) => step.id === state.progressStepId
        );
      }

      steps = steps.map((step, index) => {
        return {
          ...step, // Spread the original step data
          index,
          disabled:
            progressIndex !== -1
              ? index > progressIndex
              : (step.disabled ?? false), // If progress step is set, disable steps that come after it

          value:
            state.stepValues[step.id] ||
            getDefaultValue(step.type, step.subtype) // Add the current user value for this step from the state
        };
      });

      // Group steps by section
      const grouped = steps.reduce((acc, step) => {
        if (!acc[step.section]) {
          acc[step.section] = [];
        }
        acc[step.section].push(step);
        return acc;
      }, {});

      // Convert grouped object to array with section colors
      const sectionsArray = Object.keys(grouped).map((sectionName, index) => {
        // Get section colors from config based on the index of the section
        let backgroundColor = "";

        if (config.settings.useSectionColors) {
          backgroundColor = sectionColors[index];
        }

        return {
          name: sectionName,
          backgroundColor,
          steps: grouped[sectionName] // Add steps to the section object
        };
      });

      return sectionsArray;
    },
    // Get the current step's answer and compare it to the user's input
    checkStepAnswer:
      (state, getters) =>
      (value = "", step = "") => {
        let correct = false;

        // Get the current step
        const currentStep = step ? step : getters.getCurrentStep;
        const valueToCompare = value ? value : currentStep.value;

        if (
          currentStep &&
          !currentStep.openAnswer &&
          typeof currentStep.answer === "string"
        ) {
          correct =
            currentStep.answer.toLowerCase() === valueToCompare.toLowerCase();
        }

        if (currentStep.openAnswer) {
          // check if the value is a string and not empty
          if (
            typeof valueToCompare === "string" &&
            valueToCompare.trim() !== ""
          ) {
            correct = true;
          }
          // Check if value is an array and has at least one non-empty string
          else if (Array.isArray(valueToCompare)) {
            correct = true;
          }
        }

        return correct;
      }
  }
};

export default step;
